import { Fragment, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { LABELS } from "@match/shared";
import { PageHeader, EmptyState } from "@/components/Layout";
import { FeesPaid, OutcomePanel, PHASE_LABEL, Progress, SessionStateLabel } from "@/components/session";
import { Button, ErrorNote, inputClass, useCanWrite } from "@/components/ui";
import { api, errorMessage } from "@/lib/api";
import { lots, money, percent, points, signClass } from "@/lib/format";
import { useClients, useRefreshDirectory, useSessions } from "@/lib/queries";
import type { SessionListItem } from "@/lib/types";

const TABS = [
  { key: "live", label: "Attive", states: ["ACTIVE", "PAUSED"] },
  { key: "completed", label: LABELS.sessionState.COMPLETED, states: ["COMPLETED"] },
  { key: "failed", label: LABELS.sessionState.FAILED, states: ["FAILED"] },
  { key: "all", label: "Tutte", states: [] as string[] },
] as const;

export function Sessions() {
  const canWrite = useCanWrite();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = TABS.find((t) => t.key === params.get("tab")) ?? TABS[0];
  const clientId = params.get("cliente") ?? "";
  const phase = params.get("fase") ?? "";
  const clients = useClients();
  const sessions = useSessions({ state: [...tab.states], clientId: clientId || undefined, phase: phase || undefined });
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [outcome, setOutcome] = useState<{ kind: "complete" | "fail"; s: SessionListItem } | null>(null);

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  return (
    <>
      <PageHeader
        title="Fasi"
        subtitle="Prop contro broker dentro il ciclo, fase per fase"
        actions={canWrite && <Button variant="primary" onClick={() => navigate("/fasi/nuova")}>Nuova fase</Button>}
      />

      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-6">
        <nav className="flex gap-1" aria-label="Stato delle fasi">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setParam("tab", t.key === "live" ? "" : t.key)}
              aria-current={t.key === tab.key ? "page" : undefined}
              className={[
                "-mb-px border-b-2 px-3 py-2.5 text-sm transition-colors",
                t.key === tab.key ? "border-ink font-medium text-ink" : "border-transparent text-muted hover:text-ink",
              ].join(" ")}
            >
              {t.label}
            </button>
          ))}
        </nav>
        <div className="flex gap-2 py-2">
          <select aria-label="Cliente" value={clientId} onChange={(e) => setParam("cliente", e.target.value)} className={`${inputClass} w-48 py-1.5`}>
            <option value="">Tutti i clienti</option>
            {(clients.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <select aria-label="Fase del ciclo" value={phase} onChange={(e) => setParam("fase", e.target.value)} className={`${inputClass} w-36 py-1.5`}>
            <option value="">Tutte le fasi del ciclo</option>
            {(["PHASE_1", "PHASE_2", "FUNDED"] as const).map((p) => (
              <option key={p} value={p}>
                {PHASE_LABEL(p)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {sessions.isLoading ? (
        <p className="px-6 py-6 text-sm text-muted">Caricamento…</p>
      ) : sessions.error ? (
        <div className="px-6 py-6">
          <ErrorNote>{errorMessage(sessions.error)}</ErrorNote>
        </div>
      ) : !sessions.data?.length ? (
        clientId || phase || tab.key !== "live" ? (
          <EmptyState title="Nessuna fase con questi filtri" hint="Cambia scheda, cliente o fase del ciclo per vedere le altre." />
        ) : (
          <EmptyState
            title="Nessuna fase attiva"
            hint="Una fase lega il conto prop di un cliente al suo conto broker e fissa lotto di partenza, moltiplicatore, stop loss e take profit. Servono i due conti connessi e il simbolo mappato su entrambi i server."
            action={canWrite && <Button variant="primary" onClick={() => navigate("/fasi/nuova")}>Nuova fase</Button>}
          />
        )
      ) : (
        <div className="overflow-x-auto px-6 py-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line text-left text-xs text-faint">
                <th className="w-8 py-2" />
                <th className="py-2 pr-4 font-medium">Cliente</th>
                <th className="py-2 pr-4 font-medium">Fase</th>
                <th className="py-2 pr-4 font-medium">Simboli</th>
                <th className="py-2 pr-4 text-right font-medium">Coppie</th>
                <th className="py-2 pr-4 text-right font-medium text-broker">P&L broker</th>
                <th className="py-2 pr-4 text-right font-medium text-prop">P&L prop</th>
                <th className="w-64 py-2 pr-4 font-medium">Avanzamento</th>
                <th className="py-2 pr-4 font-medium">Stato</th>
                <th className="py-2">
                  <span className="sr-only">Azioni</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {sessions.data.map((s) => {
                const open = expanded.has(s.id);
                return (
                  <Fragment key={s.id}>
                    <SessionRow
                      s={s}
                      open={open}
                      canWrite={canWrite}
                      onToggle={() =>
                        setExpanded((prev) => {
                          const next = new Set(prev);
                          if (next.has(s.id)) next.delete(s.id);
                          else next.add(s.id);
                          return next;
                        })
                      }
                      onOutcome={(kind) => setOutcome({ kind, s })}
                    />
                    {open && (
                      <tr className="border-b border-line">
                        <td />
                        <td colSpan={9} className="pb-4">
                          <ParamsGrid s={s} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {outcome && (
        <OutcomePanel
          kind={outcome.kind}
          session={{
            id: outcome.s.id,
            clientName: outcome.s.cycle.client.name,
            phase: outcome.s.cycle.currentPhase,
            presetPhases: null,
            brokerPnl: outcome.s.summary.pnl.broker,
            brokerCurrency: outcome.s.brokerAccount.currency,
            propCurrency: outcome.s.propAccount.currency,
            reportingCurrency: outcome.s.reportingCurrency,
            openPairs: outcome.s.summary.openPairs,
          }}
          onClose={() => setOutcome(null)}
        />
      )}
    </>
  );
}

function SessionRow({
  s,
  open,
  canWrite,
  onToggle,
  onOutcome,
}: {
  s: SessionListItem;
  open: boolean;
  canWrite: boolean;
  onToggle: () => void;
  onOutcome: (kind: "complete" | "fail") => void;
}) {
  const refresh = useRefreshDirectory();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const live = s.state === "ACTIVE" || s.state === "PAUSED";
  const sum = s.summary;
  const propCur = s.propAccount.currency;
  const brokerCur = s.brokerAccount.currency;

  const togglePause = async () => {
    setBusy(true);
    setError(null);
    try {
      if (s.state === "ACTIVE") await api.sessions.pause(s.id);
      else await api.sessions.resume(s.id);
      await refresh();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <tr className={`border-line/60 align-top hover:bg-raised/40 ${open ? "" : "border-b"}`}>
        <td className="py-3">
          <button
            onClick={onToggle}
            aria-expanded={open}
            aria-label={open ? "Nascondi i parametri" : "Mostra i parametri"}
            className="rounded p-0.5 text-muted hover:bg-raised hover:text-ink"
          >
            <ChevronRight className={`h-4 w-4 transition-transform ${open ? "rotate-90" : ""}`} />
          </button>
        </td>
        <td className="py-3 pr-4">
          <Link to={`/fasi/${s.id}`} className="font-medium text-ink hover:underline">
            {s.cycle.client.name}
          </Link>
          <p className="text-xs text-faint">
            {s.cycle.preset.propFirm.name} {s.cycle.preset.name}
          </p>
        </td>
        <td className="py-3 pr-4">{PHASE_LABEL(s.phase)}</td>
        <td className="py-3 pr-4">
          <span className="text-prop">{s.symbols.prop ?? s.canonicalSymbol}</span>
          <span className="text-faint"> · </span>
          <span className="text-broker">{s.symbols.broker ?? s.canonicalSymbol}</span>
        </td>
        <td className="py-3 pr-4 text-right">
          <span className="text-ink">{sum.executedOps}</span>
          {sum.openPairs > 0 && <span className="block text-xs text-gain">{sum.openPairs} aperta</span>}
        </td>
        <td className={`py-3 pr-4 text-right ${signClass(sum.pnl.broker)}`}>{money(sum.pnl.broker, brokerCur)}</td>
        <td className={`py-3 pr-4 text-right ${signClass(sum.pnl.prop)}`}>{money(sum.pnl.prop, propCur)}</td>
        <td className="space-y-2 py-3 pr-4">
          <Progress
            label="DD usato"
            tone="prop"
            approximate={sum.ddUsedIsApproximate}
            ratio={sum.ddUsedRatio}
            detail={`${money(sum.ddUsed, propCur)} di ${money(sum.maxDdMoney, propCur)}`}
          />
          <Progress
            label="Target"
            ratio={sum.targetRatio}
            detail={sum.targetMoney === null ? "—" : `${money(sum.targetReached, propCur)} di ${money(sum.targetMoney, propCur)}`}
          />
          <Progress
            label={LABELS.terms.costsRecovered}
            tone="broker"
            ratio={sum.costsRecoveredRatio}
            approximate={sum.costsRecoveredFxAssumed}
            approximateTitle="Una coppia chiusa senza tasso broker->prop è entrata a tasso 1: stima fino alla chiusura della fase."
            detail={`${money(sum.costsRecovered, s.cycle.currency)} di ${money(sum.recoverTarget, s.cycle.currency)}`}
          />
        </td>
        <td className="py-3 pr-4">
          <SessionStateLabel state={s.state} />
        </td>
        <td className="py-3">
          <div className="flex flex-col items-end gap-1">
            <Link to={`/fasi/${s.id}`} className="rounded px-2 py-1 text-xs font-medium text-muted hover:bg-raised hover:text-ink">
              Apri
            </Link>
            {canWrite && live && (
              <>
                <Link to={`/fasi/${s.id}?tab=parametri`} className="rounded px-2 py-1 text-xs font-medium text-muted hover:bg-raised hover:text-ink">
                  Modifica parametri
                </Link>
                <Button size="sm" variant="ghost" disabled={busy} onClick={() => void togglePause()}>
                  {s.state === "ACTIVE" ? "Pausa" : "Riprendi"}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => onOutcome("complete")}>
                  {LABELS.sessionState.COMPLETED}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => onOutcome("fail")}>
                  {LABELS.sessionState.FAILED}
                </Button>
              </>
            )}
          </div>
        </td>
      </tr>
      {error && (
        <tr>
          <td colSpan={10} className="pb-2 text-xs text-loss">
            {error}
          </td>
        </tr>
      )}
    </>
  );
}

function ParamsGrid({ s }: { s: SessionListItem }) {
  const item = (label: string, value: React.ReactNode) => (
    <div>
      <dt className="text-xs text-faint">{label}</dt>
      <dd className="mt-0.5 text-ink">{value}</dd>
    </div>
  );
  return (
    <dl className="grid grid-cols-2 gap-x-6 gap-y-3 rounded-card border border-line bg-surface p-4 text-sm sm:grid-cols-4 lg:grid-cols-6">
      {item("Oggi", `${s.summary.opsToday} ${s.summary.opsToday === 1 ? "operazione" : "operazioni"}`)}
      {/* Per simbolo: lotti di strumenti diversi non si sommano. */}
      {item(
        "Volumi",
        s.summary.volumeBySymbol.length ? (
          <ul>
            {s.summary.volumeBySymbol.map((v) => (
              <li key={v.canonicalSymbol}>
                {v.canonicalSymbol} · <span className="text-prop">{lots(v.prop)}</span> / <span className="text-broker">{lots(v.broker)}</span>
              </li>
            ))}
          </ul>
        ) : (
          "—"
        ),
      )}
      {item("Taglia", money(s.cycle.accountSize, s.propAccount.currency))}
      {item("Max DD", `${percent(s.cycle.maxDdPct)} · ${LABELS.ddBasis[s.cycle.maxDdBasis]}`)}
      {item("DD giornaliero", percent(s.dailyDdPct))}
      {item("Target di fase", percent(s.challengeTargetPct))}
      {item(LABELS.terms.recoverAmount, `${s.summary.costsRecoveredFxAssumed ? "≈ " : ""}${money(s.summary.recoverRemaining, s.cycle.currency)}`)}
      {item("Fee pagate", <FeesPaid value={s.cycle.feesPaid} currency={s.cycle.currency} info={s.feesPaidInfo} />)}
    </dl>
  );
}
